import React, { useEffect, useMemo, useRef } from 'react';
import { AddAttachmentsBrushaway } from '@ui';
import { translate } from '@utils/i18n';
import { useAttachmentsUpload } from '@features/Attachments/hooks/useAttachmentsUpload';
import { useUploadProgressToast } from '@features/Attachments/hooks/useUploadProgressToast';
import { createDirectUploadTransport } from '@features/SubcontractorDocuments/services/directUploadTransport';
import { buildAddAttachmentsColumns, buildAddAttachmentsLabels, SUBCONTRACTOR_DEFAULT_UPLOAD_ATTRIBUTES, toSubcontractorQueueFields } from '@features/SubcontractorDocuments/utils/addAttachmentsConfig';
import { PROGRESS_TOAST_NS, SUBCONTRACTOR_DOCUMENTS_ENTITY_KEY, SUBCONTRACTOR_DOCUMENTS_NS } from '@features/SubcontractorDocuments/constants';
/**
 * The prime's add-documents drawer — the same presign → PUT → finalize path the subcontractor
 * side uses, reached from a document type's group header.
 *
 * It exists separately from `AttachmentsSurface` because the prime does not render that surface:
 * it keeps the legacy column set (Review Status, show-on-report, the Deficiencies AI entry points)
 * inside the `@ui` grouped table. What it needs is the drawer alone, so the upload half is lifted
 * out and the table half is not.
 *
 * Keyed by document type AND subcontractor: `useAttachmentsUpload` owns one queue, and a drawer
 * reopened for a different group must not inherit the previous group's staged files.
 */
function PrimeAddDocumentsDrawerInner({ group, uploaderTenantId, parentType, parentId, onClose, onUploaded }) {
    const parent = useMemo(() => ({ parentType, parentId }), [parentType, parentId]);
    const transport = useMemo(() => createDirectUploadTransport(parent, group.documentTypeId, uploaderTenantId), [parent, group.documentTypeId, uploaderTenantId]);
    const labels = useMemo(() => buildAddAttachmentsLabels(group.maxUploadSizeBytes, group.acceptContentTypes), [group.maxUploadSizeBytes, group.acceptContentTypes]);
    const accept = useMemo(() => { var _a; return (_a = group.acceptContentTypes) === null || _a === void 0 ? void 0 : _a.join(','); }, [group.acceptContentTypes]);
    const upload = useAttachmentsUpload({
        entityKey: `${SUBCONTRACTOR_DOCUMENTS_ENTITY_KEY}:${group.documentTypeId}`,
        ownerId: group.documentTypeId,
        transport,
        defaultAttributes: SUBCONTRACTOR_DEFAULT_UPLOAD_ATTRIBUTES,
        toFields: toSubcontractorQueueFields,
        t: (key) => translate(key, { namespace: SUBCONTRACTOR_DOCUMENTS_NS }),
        successToast: (count) => translate('toasts.success', { namespace: 'features.subcontractor_documents.upload', count })
    });
    const { openDrawer, isOpen, isUploading } = upload;
    // The subcontractor side gets this from `AttachmentsSurface`, which the prime does not render.
    // Without it a backgrounded batch is invisible and unreachable: no progress, and a failure is
    // silent because the drawer that would show it is closed. Its action reopens the LIVE queue.
    useUploadProgressToast({
        entityKey: `${SUBCONTRACTOR_DOCUMENTS_ENTITY_KEY}:${group.documentTypeId}`,
        ownerId: group.documentTypeId,
        namespace: PROGRESS_TOAST_NS,
        isOpen,
        items: upload.items,
        isUploading,
        aggregateProgress: upload.aggregateProgress,
        openDrawer
    });
    // The hook owns `isOpen`, so opening is an effect on being mounted with a group rather than a
    // prop. Mount/unmount is driven by the parent's `group` state.
    useEffect(() => {
        openDrawer();
    }, [openDrawer]);
    // `DocumentsList` holds its own React Query cache for the legacy documents endpoint, which the
    // drawer's own invalidation does not reach. Told once per completed file, keyed on the count so
    // a re-render while the queue sits idle does not refetch again.
    const doneCount = upload.items.filter((item) => item.status === 'done').length;
    const lastReportedRef = useRef(0);
    useEffect(() => {
        if (isUploading || doneCount === lastReportedRef.current)
            return;
        lastReportedRef.current = doneCount;
        if (doneCount > 0)
            onUploaded();
    }, [isUploading, doneCount, onUploaded]);
    // `isOpen` is false for the first render -- `openDrawer` above only takes effect on the next one
    // -- so "not open" means "closed by the user" only after it has actually been open. Without the
    // latch this effect fired on mount and told the parent to unmount the drawer it had just asked
    // for, and nothing ever appeared.
    const hasOpenedRef = useRef(false);
    // `!isUploading` is what makes "close to background" mean it. `onClose` here is the PARENT's --
    // it clears `primeAddTarget`, unmounting this drawer and with it `useDirectUpload`, whose unmount
    // cleanup aborts every request still in flight. Closing and backgrounding are indistinguishable
    // from `isOpen` alone, so the queue state is what separates them. When the batch settles this
    // effect re-runs and the deferred unmount happens then.
    useEffect(() => {
        if (isOpen) {
            hasOpenedRef.current = true;
            return;
        }
        if (hasOpenedRef.current && !isUploading)
            onClose();
    }, [isOpen, isUploading, onClose]);
    return (React.createElement(AddAttachmentsBrushaway, { open: isOpen, items: upload.items, isUploading: upload.isUploading, aggregateProgress: upload.aggregateProgress, onFilesSelected: upload.onFilesSelected, onNameChange: upload.onNameChange, onRemoveItem: upload.onRemoveItem, onRetryItem: upload.onRetryItem, onRetryFailed: upload.onRetryFailed, onCancelUpload: upload.onCancelUpload, onSubmit: upload.onSubmit, onClose: upload.onClose, onCloseToBackground: upload.onCloseToBackground, maxSizeBytes: group.maxUploadSizeBytes, accept: accept, columns: buildAddAttachmentsColumns(), labels: labels }));
}
export function PrimeAddDocumentsDrawer({ group, uploaderTenantId, parentType, parentId, onClose, onUploaded }) {
    if (!group)
        return null;
    return (React.createElement(PrimeAddDocumentsDrawerInner, { key: `${group.documentTypeId}-${String(uploaderTenantId)}`, group: group, uploaderTenantId: uploaderTenantId, parentType: parentType, parentId: parentId, onClose: onClose, onUploaded: onUploaded }));
}
export default PrimeAddDocumentsDrawer;
